/**
 * outputSeries — production-agnostic shaping for a Plan / Prognose / Ist
 * output-over-time surface (#2341). Deliberately domain-neutral: the first
 * instance is crop harvest (Ernteprognose), but a future animal-yield tile
 * (milk, eggs) is a sibling on the exact same spine, so nothing here names
 * ``harvest``/``field``/``crop`` — only ``output``, ``actual``, ``forecast``,
 * ``plan``.
 *
 * All functions are pure (no React, no network, no recharts — recharts does
 * not render under jsdom) so the day/month/year grain switch, the cumulative
 * line building, and the Ampel verdict are unit-tested directly.
 */

import {
  addDays,
  addMonthsToYm,
  firstDayOfMonth,
  lastDayOfMonth,
} from '../liquiditaetsplanung/computeBalanceSeries';

/** Client-side rollup grain for the output-over-time chart. */
export type Grain = 'day' | 'week' | 'month' | 'year';

/** A 'YYYY-MM' inclusive window (the Juli–Juli Wirtschaftsjahr). */
export interface OutputWindow {
  startYm: string;
  endYm: string;
}

/** One toggleable prior-year overlay: per-day Ist already shifted into the
 *  current WJ (day-of-season aligned). ``offset`` is 1/2/3 (−1/−2/−3 Jahr). */
export interface PriorYearSeries {
  offset: number;
  points: { date: string; value: number }[];
}

/** A dated actual output quantity (e.g. one berry pick). */
export interface OutputActual {
  date: string; // ISO ``YYYY-MM-DD`` (or a longer ISO instant — only the date part is read)
  qty: number;
  unit?: string | null;
  /** Member (crop_cycle) this actual belongs to (#2455): lets the aggregate seed
   *  each member's forecast from its OWN Ist. Omitted ⇒ one shared series. */
  seriesId?: string | null;
}

/** A dated forecast point (a current-state Prognose value for a target day). */
export interface OutputForecastPoint {
  forecastDate: string; // ISO ``YYYY-MM-DD``
  value: number;
  measureKind?: string | null;
  /** Member (crop_cycle) this forecast belongs to (#2455): the aggregate seeds
   *  and cumulates each member separately, then sums. Omitted ⇒ one shared series. */
  seriesId?: string | null;
}

/** A seasonal plan target — a single scalar for the plan period, not a series. */
export interface OutputPlanTarget {
  value: number;
  measureKind?: string | null;
  planPeriod?: string | null;
}

/**
 * Which numbers the chart shows (#2544-B): ``cumulative`` = the running season
 * total ("wo steht die Saison"), ``perPeriod`` = the amount harvested IN each
 * bucket ("wie stark wird gerade gepflückt, und wann waren die Spitzen").
 *
 * A DATA axis, not a presentation one: it decides WHAT the numbers are, never
 * how they are drawn.
 */
export type ChartMode = 'cumulative' | 'perPeriod';

/**
 * One x-axis bucket of the chart, carrying BOTH semantics: the ``*Cum`` running
 * totals and the ``*Bucket`` per-period amounts (#2544-B).
 *
 * Deliberately ONE row type rather than a row array per mode. The shared zoom
 * primitive (``components/charts/useChartTimeWindow``) holds an INDEX window and
 * resets it whenever ``rows.length`` changes — so a second, per-mode array would
 * silently throw away the farmer's zoom and selection on every mode switch. The
 * two field families are computed from the same per-bucket sums, so the
 * Einzelernte plot and the "Ernte im Zeitraum" readout column that #2544-C will
 * add are guaranteed to be one source rather than two derivations.
 */
export interface OutputChartRow {
  bucket: string; // grain bucket key: ``YYYY-MM-DD`` | ``YYYY-MM`` | ``YYYY``
  label: string; // human display label for the bucket
  /** Cumulative actual (Ist) through this bucket; null outside the actual data range. */
  actualCum: number | null;
  /** Cumulative forecast (Prognose) through this bucket; null outside the forecast data range. */
  forecastCum: number | null;
  /** Cumulative horizon-overlay forecast ("Prognose vor N Tagen"); null outside its range / when disabled. */
  horizonCum: number | null;
  /** Flat seasonal plan target, constant across buckets; null when no plan exists (grey the Plan line). */
  planTarget: number | null;
  /** Cumulative prior-year Ist overlays (day-of-season aligned), null outside range / when toggled off. */
  priorYear1: number | null;
  priorYear2: number | null;
  priorYear3: number | null;
  /** Ist harvested IN this bucket (not a running total); null outside the actual data range. */
  actualBucket: number | null;
  /**
   * Prognose expected IN this bucket — the RAW per-bucket sum, never
   * ``diff(forecastCum)``. See {@link bucketWithinRange} for why the cumulative
   * is not differenceable here (seed + clamps + render bridge).
   */
  forecastBucket: number | null;
  /** Horizon-overlay forecast IN this bucket; null outside its range / when disabled. */
  horizonBucket: number | null;
  /** Prior-year Ist harvested IN this bucket (day-of-season aligned). */
  priorYear1Bucket: number | null;
  priorYear2Bucket: number | null;
  priorYear3Bucket: number | null;
  // NOTE — there is deliberately no ``planBucket``. ``OutputPlanTarget`` is a
  // single seasonal SCALAR (``yield_plan`` stores a season target, not a curve),
  // so spreading it across buckets would invent a plan shape nobody entered. The
  // Plan series is hidden entirely in Einzelernte instead.
}

export type OutputVerdictLevel = 'green' | 'yellow' | 'red' | 'neutral';

export interface OutputVerdict {
  level: OutputVerdictLevel;
  /** Short Ampel word — always paired with an icon + colour, never colour alone. */
  headline: string;
  /** Full German du-form sentence with the figures. */
  sentence: string;
  actualToDate: number | null;
  forecastToDate: number | null;
  ratio: number | null;
  atBucket: string | null;
}

// ── ISO week helpers (#2544-A) ────────────────────────────────────────────
//
// German standard weeks are DIN 1355-1 ≡ ISO 8601: Monday-start, and a week
// belongs to the year that owns its THURSDAY (so KW 1 can begin in December and
// a calendar 01.01 can still be KW 52/53 of the previous week-year).
//
// These two helpers are DEFINED HERE, not in ``components/charts/``, because the
// week grain's load-bearing consumer is bucketing: ``bucketKey`` decides which
// pick lands in which column. A second, independently-written week helper next
// to the shared axis primitives could disagree by a day and silently mis-bucket
// harvest, so there is exactly one definition and the chart layer reuses it.
// (Dedup with any future shared time-domain primitive is tracked as follow-up.)
//
// All arithmetic is UTC via ``addDays``/``Date.UTC`` — a local-timezone Date
// would shift a Monday across midnight for anyone west of Greenwich.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Midnight-UTC epoch ms of an ISO ``YYYY-MM-DD`` (longer instants are truncated). */
const utcMs = (dateIso: string): number => Date.parse(`${dateIso.slice(0, 10)}T00:00:00Z`);

/**
 * Monday of the ISO week containing ``dateIso`` (UTC), as ``YYYY-MM-DD``.
 * Idempotent: a Monday maps to itself; a Sunday maps BACK six days, never
 * forward (an ISO week ends on Sunday, it does not start on one).
 *
 * An unparseable date is returned AS ITS OWN bucket rather than thrown on. The
 * other grains are pure string slices, so they already tolerate junk: a single
 * malformed pick date must not white-screen the page at week grain only. Pick
 * dates reach ``bucketKey`` unvalidated (``useErnteprognose`` only null-checks
 * them), and a non-zero-padded ``2026-8-3`` is a shape real data does produce.
 */
export function isoWeekStart(dateIso: string): string {
  const d = dateIso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dow = new Date(`${d}T00:00:00Z`).getUTCDay(); // 0=So … 6=Sa
  if (!Number.isFinite(dow)) return d; // structurally well-formed but not a real date
  return addDays(d, -((dow + 6) % 7)); // Mo→0 … So→6
}

/**
 * ISO week number (1…53) plus the ISO WEEK-YEAR, which differs from the calendar
 * year at the rollover: 2027-01-01 is a Friday, so it is still KW 53/2026, while
 * 2025-12-29 is a Monday whose Thursday is 2026-01-01, so it is already KW 1/2026.
 *
 * Derived from the week's own Thursday (the ISO ownership rule) and then counted
 * off that week-year's KW 1, which is by definition the week containing Jan 4.
 */
export function isoWeekParts(dateIso: string): { week: number; weekYear: number } {
  const monday = isoWeekStart(dateIso);
  const thursday = addDays(monday, 3);
  const weekYear = Number(thursday.slice(0, 4));
  const firstWeekMonday = isoWeekStart(`${weekYear}-01-04`);
  const week = Math.round((utcMs(monday) - utcMs(firstWeekMonday)) / (7 * MS_PER_DAY)) + 1;
  return { week, weekYear };
}

// ── Grain bucketing ───────────────────────────────────────────────────────

/**
 * Bucket key for a date at the given grain. Day/month/year are pure string
 * slices (no Date, no tz); ``week`` is the ISO week's MONDAY as ``YYYY-MM-DD``.
 *
 * The week key is deliberately NOT ``YYYY-Www``. Every downstream consumer
 * already assumes a year-leading, lexicographically sortable, ``YYYY-MM``-
 * sliceable key: ``OutputSeriesChart`` builds its axis ticks from
 * ``bucket.slice(0, 7)`` and parses ``bucket.split('-').map(Number)``,
 * ``components/charts/yearBoundaries`` documents that contract verbatim, and
 * ``cumWithinRange``'s range clamp compares bucket keys as strings. A Monday key
 * satisfies all of them with zero downstream edits; ``2026-W35`` would slice to
 * ``2026-W3``, parse to ``NaN``, and mis-sort across the year rollover.
 */
export function bucketKey(dateIso: string, grain: Grain): string {
  switch (grain) {
    case 'day':
      return dateIso.slice(0, 10);
    case 'week':
      return isoWeekStart(dateIso);
    case 'month':
      return dateIso.slice(0, 7);
    case 'year':
      return dateIso.slice(0, 4);
  }
}

/** Display label for a bucket key (German ``DD.MM.YYYY`` / ``KW nn/YYYY`` /
 *  ``MM.YYYY`` / ``YYYY``). The week label is kept at the density of the month
 *  label so the legend/readout line lengths stay comparable across grains. */
export function bucketLabel(bucket: string, grain: Grain): string {
  if (grain === 'year') return bucket;
  if (grain === 'month') {
    const [y, m] = bucket.split('-');
    return `${m}.${y}`;
  }
  if (grain === 'week') {
    const { week, weekYear } = isoWeekParts(bucket);
    return `KW ${String(week).padStart(2, '0')}/${weekYear}`;
  }
  const [y, m, d] = bucket.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * A fuller, heading-sized label for a bucket: at week grain it spells the week
 * out Mo–So (``KW 35 (24.08.–30.08.2026)``) so the "Ausgewählte Woche" readout
 * says which DAYS the farmer is looking at, not just a week number nobody counts
 * in their head. Identical to {@link bucketLabel} at every other grain — a day,
 * a month and a year already ARE their own range.
 *
 * The year is normally printed once, on the Sunday. A week that CROSSES the year
 * boundary prints both (``KW 53 (28.12.2026–03.01.2027)``): with a single
 * trailing year that week renders as ``28.12.–03.01.2027``, which reads as
 * 28.12.2027 and contradicts ``bucketLabel``'s ``KW 53/2026`` for the same
 * bucket. Such a week sits on the ±12-month axis every single season, so this is
 * a routine bucket, not an edge case.
 */
export function bucketRangeLabel(bucket: string, grain: Grain): string {
  if (grain !== 'week') return bucketLabel(bucket, grain);
  const monday = bucket.slice(0, 10);
  const sunday = addDays(monday, 6);
  const [yMo, mMo, dMo] = monday.split('-');
  const [ySo, mSo, dSo] = sunday.split('-');
  const { week } = isoWeekParts(monday);
  const from = yMo === ySo ? `${dMo}.${mMo}.` : `${dMo}.${mMo}.${yMo}`;
  return `KW ${String(week).padStart(2, '0')} (${from}–${dSo}.${mSo}.${ySo})`;
}

/** Map a measure_kind to its short unit label. */
export function measureLabel(measureKind: string | null | undefined): string {
  if (!measureKind || measureKind === 'harvest_kg') return 'kg';
  if (measureKind.endsWith('_kg')) return 'kg';
  return measureKind;
}

/** Sum values into grain buckets. Returns ``[{ bucket, sum }]`` sorted ascending. */
export function aggregateByGrain(
  points: { date: string; value: number }[],
  grain: Grain,
): { bucket: string; sum: number }[] {
  const byBucket = new Map<string, number>();
  for (const p of points) {
    if (!Number.isFinite(p.value)) continue;
    const b = bucketKey(p.date, grain);
    byBucket.set(b, (byBucket.get(b) ?? 0) + p.value);
  }
  return [...byBucket.keys()]
    .sort()
    .map((bucket) => ({ bucket, sum: byBucket.get(bucket)! }));
}

/**
 * Cumulative value per union-bucket for one series, carried FLAT across gap
 * buckets and clamped to ``null`` strictly outside the series' own
 * ``[first .. last]`` data range (so a line neither starts at a phantom 0 nor
 * extends flat into the unknown future).
 *
 * ``seed`` (#2455) offsets the running total's starting value — the forecast
 * (Prognose) run is seeded with the Ist cumulative already reached at the
 * boundary so its curve CONTINUES the Ist curve upward instead of restarting at
 * 0 (default 0 ⇒ the plain cumulative). The clamp is unaffected: the seed only
 * shifts the values that fall WITHIN the series' own range; buckets outside it
 * stay ``null`` regardless of the seed.
 */
function cumWithinRange(
  raw: Map<string, number>,
  unionBuckets: string[],
  seed = 0,
): (number | null)[] {
  if (raw.size === 0) return unionBuckets.map(() => null);
  const keys = [...raw.keys()].sort();
  const first = keys[0];
  const last = keys[keys.length - 1];
  let run = seed;
  return unionBuckets.map((b) => {
    if (b < first || b > last) return null;
    run += raw.get(b) ?? 0; // gap bucket adds 0 → run stays flat
    return run;
  });
}

/**
 * Per-bucket (non-cumulative) value per union-bucket for one series — the
 * Einzelernte counterpart of {@link cumWithinRange}, and its clamp is the exact
 * mirror: ``0`` INSIDE the series' own ``[first .. last]`` range (a real "nothing
 * happened in this bucket"), ``null`` strictly outside it, so a column chart
 * neither draws a phantom zero floor before the first pick nor extends one into
 * the unknown future.
 *
 * Read straight off the PRE-CUMULATION sums. The cumulative arrays are NOT
 * differenceable back into this, for three independent reasons all present in
 * this module:
 *
 *   1. **The #2455 seed.** ``cumWithinRange``'s ``seed`` offsets the forecast's
 *      running total with the Ist already harvested before the forecast begins.
 *      Differencing dumps that entire seed into the FIRST forecast bucket as a
 *      phantom harvest that never happened.
 *   2. **The #2490 clamps.** ``clampForecastMonotonic`` LIFTS the cumulative
 *      forecast to the Ist at Ist-carrying buckets and floors any decrease.
 *      Differencing a clamped curve yields a spike at the lifted bucket followed
 *      by a run of artificial zeros — a display artefact, not a forecast. (Those
 *      clamps exist to stop a CUMULATIVE curve dipping under the cumulative Ist;
 *      in Einzelernte the two are drawn side by side, so the concern is moot.)
 *   3. **``withForecastJoin``.** It writes a synthetic flat ``forecastCum``
 *      bridge for RENDERING only; differencing that copy would emit zeros right
 *      across the bridge.
 *
 * Negative values are NOT floored at 0. ``aggregateByGrain`` already drops
 * non-finite values; a genuinely negative forecast increment is upstream data
 * corruption, and a column below the axis is the honest way to surface it.
 */
function bucketWithinRange(raw: Map<string, number>, unionBuckets: string[]): (number | null)[] {
  if (raw.size === 0) return unionBuckets.map(() => null);
  const keys = [...raw.keys()].sort();
  const first = keys[0];
  const last = keys[keys.length - 1];
  return unionBuckets.map((b) => (b < first || b > last ? null : raw.get(b) ?? 0));
}

/**
 * Monotonic-and-above-Ist clamp for a cumulative forecast line (#2490).
 * Cumulative harvest can never decrease, and — for the live Prognose — can never
 * sit BELOW what has already been picked (a green Prognose dipping under the blue
 * Ist is impossible). Two grain-agnostic guards: they operate on the already
 * grain-bucketed cumulative arrays, so Day / Monat / Jahr behave identically and
 * the current partial month/year is just the last Ist-carrying bucket, anchored
 * at its full Ist cumulative the same way the daily Heute bucket is.
 *
 *   - ``floorAtActual``: the forecast never sits below the CURRENT Ist
 *     cumulative — the most recent non-null ``actualCum`` at or before the
 *     bucket (#2775). Inside the Ist range that is the bucket's own Ist, dips
 *     INCLUDED: the Ist cumulative is NOT guaranteed non-decreasing (a negative
 *     storno/correction pick is a real twin shape), and pinning the floor at a
 *     stale pre-correction peak would grade an on-track harvest "im Rückstand"
 *     off a correction row — so the floor FOLLOWS the Ist, exactly #2490's
 *     per-bucket lift. Beyond the last pick, where ``cumWithinRange`` nulls the
 *     Ist and the per-bucket lift structurally could not fire, the LAST Ist is
 *     carried forward (the #2775 disjoint-range guard — a forecast starting
 *     after the season's last pick is the normal late-season state). This also
 *     kills the multi-Schlag dip where per-member overlap-window forecast
 *     increments (a member still harvesting AFTER its own first forecast date,
 *     whose overlap actuals ``summedSeed`` excludes), summed, land below the
 *     aggregate Ist. It only ever LIFTS: a bucket where the forecast already
 *     exceeds the Ist (a genuine "im Rückstand" deficit the Ampel must still
 *     surface) is left untouched. The "Prognose vor N Tagen" horizon overlay is
 *     a retrospective vintage line that is MEANT to read below today's Ist, so
 *     it keeps this off — the floor is gated with the rest of ``floorAtActual``
 *     and never touches the horizon path (#2776's contract).
 *   - running max: the forecast never decreases across buckets (guards a negative
 *     daily forecast increment). Applied to every forecast line.
 *
 * Only touches buckets where the forecast is non-null — the range clamp from
 * ``cumWithinRange`` (null strictly outside [first..last]) is preserved, so no
 * synthetic point is injected onto an Ist-only bucket and the Ampel verdict /
 * selected-day readout still read a genuine forecast, never a bridged one.
 */
function clampForecastMonotonic(
  forecastArr: (number | null)[],
  actualArr: (number | null)[],
  floorAtActual: boolean,
): (number | null)[] {
  let runningMax = -Infinity;
  // #2775 — the current-Ist floor. ASSIGNED (not maxed) at every Ist-carrying
  // index, before the null early-return: inside the Ist range it follows the
  // Ist through dips (storno rows), beyond the range it carries the LAST value
  // forward. Gated on ``floorAtActual`` so the horizon path neither pays for
  // nor is touched by it.
  let istFloor = -Infinity;
  return forecastArr.map((f, i) => {
    if (floorAtActual) {
      const a = actualArr[i];
      if (a != null) istFloor = a;
    }
    if (f == null) return null;
    let v = f;
    if (floorAtActual && istFloor > v) v = istFloor; // never below the current Ist
    if (v < runningMax) v = runningMax; // never decrease across buckets
    runningMax = v;
    return v;
  });
}

// ── Chart assembly ────────────────────────────────────────────────────────

/** Shared member key for an untagged (``seriesId``-less) point: one group-wide
 *  series (#2455). Module-scoped so the seed (``buildOutputChart``) and the
 *  coverage counters (#2775) agree on membership semantics. */
const GROUP_ALL = '__all__';
const memberKey = (id: string | null | undefined): string => id ?? GROUP_ALL;

/**
 * Runaway guards for the day/week spines (#2601/#2610) — see the loops in
 * ``wjBuckets``. The widest window Ernteprognose can now produce is 47 months
 * (≈1430 day / 205 week buckets), so these cannot truncate a legitimate spine.
 */
const MAX_DAY_BUCKETS = 2000;
const MAX_WEEK_BUCKETS = 400;

/**
 * Every bucket of a fixed Wirtschaftsjahr window at the chosen grain — the
 * FULL Jul→Jun spine, not just the buckets that carry data (#2373). Padding to
 * the whole window is what pins the x-axis: empty months stay visible and the
 * window ends at Jun, not at the last pick. Day grain ≈ 365 rows, week grain
 * ≈ 52, month grain 12, year grain the calendar years the WJ spans (2 for
 * Jul–Jun).
 *
 * The week spine starts at the Monday AT OR BEFORE the window's first day, so it
 * may begin a few days OUTSIDE the window — intentional: a pick landing in the
 * window's first days must always have a home bucket, and a truncated part-week
 * would under-report it (#2544-A). The LAST week likewise runs a few days past
 * the window end, with the mirror-image consequence: a point dated just past the
 * end has a home bucket at week grain but none at day grain — which is why the
 * two grains can differ at the very edges.
 *
 * #2601/#2610 — that used to be unreachable because actuals were pre-clamped to
 * a WJ window sitting ~12 months inside the ±1-year axis. They are not any more:
 * Ist is season-sliced with no date clamp. Ernteprognose compensates on the
 * CALLER side, widening its axis window to contain the season's own picks
 * (``ErnteprognosePage``'s ``axisWindow``), so a season's points land strictly
 * inside the spine rather than on its boundary. Callers that pass a window
 * narrower than their data keep the edge caveat above.
 */
export function wjBuckets(window: OutputWindow, grain: Grain): string[] {
  if (grain === 'year') {
    const startY = Number(window.startYm.slice(0, 4));
    const endY = Number(window.endYm.slice(0, 4));
    const out: string[] = [];
    for (let y = startY; y <= endY; y++) out.push(String(y));
    return out;
  }
  if (grain === 'month') {
    const out: string[] = [];
    let ym = window.startYm;
    // Bounded loop (guard against a malformed window) — a WJ is 12 months.
    for (let i = 0; i < 240 && ym <= window.endYm; i++) {
      out.push(ym);
      ym = addMonthsToYm(ym, 1);
    }
    return out;
  }
  const end = lastDayOfMonth(window.endYm);
  // Bounded loops, mirroring the month branch's guard (#2601/#2610): the axis
  // window is no longer a fixed 12-month WJ span — Ernteprognose widens it to
  // contain the season it draws, so a malformed/absurd window must degrade to a
  // truncated spine rather than spin. ~5.5 years of days / ~7.7 years of weeks is
  // far beyond any legitimate window and far below a hang.
  if (grain === 'week') {
    const out: string[] = [];
    let d = isoWeekStart(firstDayOfMonth(window.startYm));
    for (let i = 0; i < MAX_WEEK_BUCKETS && d <= end; i++) {
      out.push(d);
      d = addDays(d, 7);
    }
    return out;
  }
  const out: string[] = [];
  let d = firstDayOfMonth(window.startYm);
  for (let i = 0; i < MAX_DAY_BUCKETS && d <= end; i++) {
    out.push(d);
    d = addDays(d, 1);
  }
  return out;
}

/**
 * The default Heute-centred chart AXIS window (#2436): ``todayYm − 12 months …
 * todayYm + 12 months``. Widening the fixed Juli–Juli axis to a symmetric
 * ±1-year span puts the "Heute" splitter in the MIDDLE of the chart (the
 * finished past season on the left, the upcoming one on the right, instead of
 * Heute pinned to the left edge) and sets the future-pan bound at the forecast
 * horizon (today + 1 year). Only the AXIS widens — each series stays clamped to
 * its own data range by ``buildOutputChart``, so the extra span is empty padding,
 * never phantom data. ``todayYm`` is a ``YYYY-MM``.
 */
export function centeredYearWindow(todayYm: string): OutputWindow {
  return { startYm: addMonthsToYm(todayYm, -12), endYm: addMonthsToYm(todayYm, 12) };
}

/**
 * Build the cumulative Plan / Prognose / Ist (+ optional horizon + prior-year
 * overlays) chart rows at the chosen grain. Each line is a running total over
 * its own buckets; the Plan target is a flat scalar across the whole window.
 *
 * When ``window`` is given the x-domain is PADDED to that full Wirtschaftsjahr
 * (fixed Juli–Juli axis, #2373); without it the domain is the union of buckets
 * that carry data (the v1 behaviour). Each series is still clamped to its OWN
 * [first..last] range, so a line neither starts at a phantom 0 nor extends flat
 * into the future — the padding only widens the axis, never the lines.
 */
export function buildOutputChart(args: {
  actuals: OutputActual[];
  forecasts: OutputForecastPoint[];
  plan: OutputPlanTarget | null;
  grain: Grain;
  horizonPoints?: OutputForecastPoint[];
  window?: OutputWindow | null;
  priorYears?: PriorYearSeries[];
}): OutputChartRow[] {
  const { actuals, forecasts, plan, grain, horizonPoints, window, priorYears } = args;

  const sumByBucket = (points: { date: string; value: number }[]): Map<string, number> => {
    const m = new Map<string, number>();
    for (const { bucket, sum } of aggregateByGrain(points, grain)) m.set(bucket, sum);
    return m;
  };

  const aRaw = sumByBucket(actuals.map((a) => ({ date: a.date, value: a.qty })));
  const fRaw = sumByBucket(forecasts.map((f) => ({ date: f.forecastDate, value: f.value })));
  const hRaw = horizonPoints
    ? sumByBucket(horizonPoints.map((h) => ({ date: h.forecastDate, value: h.value })))
    : null;
  const pyRaw = (priorYears ?? []).map((py) => ({ offset: py.offset, raw: sumByBucket(py.points) }));

  const union = window
    ? wjBuckets(window, grain)
    : [
        ...new Set<string>([
          ...aRaw.keys(),
          ...fRaw.keys(),
          ...(hRaw ? hRaw.keys() : []),
          ...pyRaw.flatMap((p) => [...p.raw.keys()]),
        ]),
      ].sort();

  const actualArr = cumWithinRange(aRaw, union);
  // Bug 2 (#2455): a cumulative forecast line (the Prognose, and the "Prognose vor
  // N Tagen" horizon overlay) must CONTINUE from the current Ist harvest, not
  // restart at 0. Seed the forecast run with the Ist cumulative already reached
  // STRICTLY BEFORE the series begins — the "Heute boundary" cumulative — summed
  // at DAY resolution from the raw dates (not the coarsened grain buckets) so it
  // is correct at month/year grain too, and so a forecast increment sharing the
  // boundary day is never double-counted on top of that day's own Ist.
  //
  // The aggregate chart flattens every current-WJ member (crop_cycle) into one
  // ``forecasts`` list. Seed PER MEMBER — each from its OWN Ist before its OWN
  // first forecast date — then SUM the seeds into one baseline. That keeps a
  // later-starting member's already-harvested Ist in the baseline (a single
  // group-wide "before the earliest forecast date" seed would drop it and leave
  // the aggregate Prognose below aggregate Ist), and because the whole aggregate
  // then runs as ONE monotonic cumulative it can never dip when one member's
  // forecast ends before another's. Members that harvest WITHOUT forecasting
  // join the line too, per bucket as their picks land (#2775 — see
  // ``widenedIstCum`` below). No ``seriesId`` ⇒ one shared group (single-crop /
  // per-field path, unchanged); an untagged group-wide series (e.g. the horizon
  // overlay, which carries no crop_cycle) seeds from all actuals so it stays
  // comparable to the main Prognose at its start.
  const summedSeed = (points: OutputForecastPoint[]): number => {
    if (points.length === 0) return 0;
    const byMember = new Map<string, OutputForecastPoint[]>();
    for (const p of points) {
      const k = memberKey(p.seriesId);
      const bucket = byMember.get(k);
      if (bucket) bucket.push(p);
      else byMember.set(k, [p]);
    }
    let seed = 0;
    for (const [k, memberPoints] of byMember) {
      const firstDate = memberPoints.reduce<string | null>((min, p) => {
        const d = p.forecastDate.slice(0, 10);
        return min == null || d < min ? d : min;
      }, null);
      if (firstDate == null) continue;
      const memberActuals = k === GROUP_ALL ? actuals : actuals.filter((a) => memberKey(a.seriesId) === k);
      for (const a of memberActuals) {
        if (Number.isFinite(a.qty) && a.date.slice(0, 10) < firstDate) seed += a.qty;
      }
    }
    return seed;
  };
  // #2775 — the per-bucket widening for members that harvest WITHOUT
  // forecasting. ``summedSeed``'s ``byMember`` is keyed off the FORECAST
  // points, so a Schlag with picks but no Prognose never entered the baseline
  // and the aggregate Prognose cliffed below the Ist (WF Erdbeeren: 27 pick,
  // 13 forecast → 356.671 kg under — a LIVE shape: season-spanning forecasts
  // start in the past while non-forecasting Schläge keep picking). Their Ist
  // joins the forecast line PER BUCKET, as the picks land — a flat season-total
  // seed would claim, at early forecast buckets, harvest that has not happened
  // yet. Season end still carries their full Ist (the anti-cliff guarantee).
  //
  // Returns null (no widening) when GROUP_ALL is a forecast key: an untagged
  // group's own seed already covers ALL actuals (the horizon overlay's shape —
  // untagged vintage points over tagged actuals), so widening would count every
  // pick twice. NB: for a hypothetical MIXED tagged+untagged forecast list this
  // guard makes the untagged key stand for group-wide coverage — a latent trap
  // no current caller exercises (#2775 follow-up).
  const widenedIstCum = (points: OutputForecastPoint[]): number[] | null => {
    if (points.length === 0) return null;
    const keys = new Set(points.map((p) => memberKey(p.seriesId)));
    if (keys.has(GROUP_ALL)) return null;
    const raw = sumByBucket(
      actuals
        .filter((a) => !keys.has(memberKey(a.seriesId)))
        .map((a) => ({ date: a.date, value: a.qty })),
    );
    if (raw.size === 0) return null;
    let acc = 0;
    return union.map((b) => {
      acc += raw.get(b) ?? 0;
      return acc;
    });
  };
  /** Add a per-bucket widening onto a cumulative line; null buckets stay null
   *  (the range clamp is preserved — the widening never extends a line). */
  const addCum = (arr: (number | null)[], add: number[] | null): (number | null)[] =>
    add == null ? arr : arr.map((v, i) => (v == null ? null : v + add[i]));
  // #2490: the summed forecast can dip below the aggregate Ist near "Heute" when a
  // member harvests INTO its own forecast-overlap window (``summedSeed`` excludes
  // those overlap actuals, cumulating the under-predicting model increments
  // instead). Clamp the Prognose to never fall below the Ist and never decrease —
  // grain-agnostic, so Day/Monat/Jahr all hold.
  const forecastArr = clampForecastMonotonic(
    addCum(cumWithinRange(fRaw, union, summedSeed(forecasts)), widenedIstCum(forecasts)),
    actualArr,
    true,
  );
  // Seeding BOTH series from their own pre-start Ist keeps the horizon overlay
  // comparable to the main Prognose AT ITS VINTAGE START. Further out the two
  // are deliberately NOT bucket-for-bucket comparable under partial coverage
  // (#2775): the main line carries non-forecasting members' picks as they land
  // (``widenedIstCum``) and floors at the current Ist, while the vintage line
  // is a FROZEN prediction — it absorbs nothing after its start (no widening,
  // ``floorAtActual = false``, only the running-max guard), which is exactly
  // why it may legitimately read below today's Ist (#2776's contract).
  const horizonArr = hRaw
    ? clampForecastMonotonic(cumWithinRange(hRaw, union, summedSeed(horizonPoints ?? [])), actualArr, false)
    : null;
  const pyArr = pyRaw.map((p) => ({ offset: p.offset, arr: cumWithinRange(p.raw, union) }));
  const planTarget = plan ? plan.value : null;

  // #2544-B Einzelernte — the per-bucket amounts, taken from the SAME ``*Raw``
  // maps the cumulative arrays consume (never differenced back out of them; see
  // ``bucketWithinRange``). Additive fields on the same rows, so the mode switch
  // preserves the zoom window and the selection.
  const actualBucketArr = bucketWithinRange(aRaw, union);
  const forecastBucketArr = bucketWithinRange(fRaw, union);
  const horizonBucketArr = hRaw ? bucketWithinRange(hRaw, union) : null;
  const pyBucketArr = pyRaw.map((p) => ({ offset: p.offset, arr: bucketWithinRange(p.raw, union) }));

  const py = (i: number, offset: number): number | null =>
    pyArr.find((p) => p.offset === offset)?.arr[i] ?? null;
  const pyBucket = (i: number, offset: number): number | null =>
    pyBucketArr.find((p) => p.offset === offset)?.arr[i] ?? null;

  return union.map((bucket, i) => ({
    bucket,
    label: bucketLabel(bucket, grain),
    actualCum: actualArr[i],
    forecastCum: forecastArr[i],
    horizonCum: horizonArr ? horizonArr[i] : null,
    planTarget,
    priorYear1: py(i, 1),
    priorYear2: py(i, 2),
    priorYear3: py(i, 3),
    actualBucket: actualBucketArr[i],
    forecastBucket: forecastBucketArr[i],
    horizonBucket: horizonBucketArr ? horizonBucketArr[i] : null,
    priorYear1Bucket: pyBucket(i, 1),
    priorYear2Bucket: pyBucket(i, 2),
    priorYear3Bucket: pyBucket(i, 3),
  }));
}

/**
 * Bug 2 (#2455) visual join. With ``connectNulls={false}`` the Prognose line only
 * draws where ``forecastCum`` is non-null, so a future-only forecast leaves a gap
 * after the last Ist point even once its run is seeded. Return a RENDER copy that
 * carries the Ist cumulative FLAT — ``forecastCum = the boundary's actualCum`` —
 * from the last Ist bucket before the forecast up to (but not including) the
 * forecast's first bucket. The dashed Prognose line then starts exactly where the
 * solid Ist line ends (a shared point at the boundary) and stays connected across
 * any gap between the last pick and the forecast start — a single isolated point
 * would not, since ``connectNulls={false}`` + ``dot={false}`` render nothing when
 * the neighbouring buckets are null.
 *
 * No-op (returns the input array) when there is nothing to bridge: no forecast,
 * a forecast that already overlaps the Ist range (starts at bucket 0 or shares a
 * bucket with actuals), or a seed-0 forecast with no actuals before it. Pure and
 * non-mutating.
 *
 * Render-ONLY on purpose: this is applied to the chart's LINE data, never folded
 * into the rows the Ampel verdict or the selected-day readout read — so the
 * synthetic bridge is never mistaken for a real Ist-vs-Prognose comparison.
 */
export function withForecastJoin(rows: OutputChartRow[]): OutputChartRow[] {
  const forecastStart = rows.findIndex((r) => r.forecastCum != null);
  // No forecast, or it already starts at the very first bucket (nothing before it
  // to join back to).
  if (forecastStart <= 0) return rows;
  // The forecast already overlaps the Ist range at its own first bucket — both
  // series share that point, so there is nothing to bridge (bridging would draw a
  // synthetic dashed segment into the PRECEDING Ist bucket, before the real
  // forecast starts).
  if (rows[forecastStart].actualCum != null) return rows;
  // The last Ist bucket strictly before the forecast begins = the boundary.
  let boundary = -1;
  for (let i = 0; i < forecastStart; i++) {
    if (rows[i].actualCum != null) boundary = i;
  }
  // No actuals before the forecast (seed-0 case) → nothing to bridge from.
  if (boundary < 0) return rows;
  const seed = rows[boundary].actualCum; // the Ist cumulative at the boundary
  const out = rows.slice();
  for (let i = boundary; i < forecastStart; i++) {
    out[i] = { ...out[i], forecastCum: seed };
  }
  return out;
}

// ── Forecast coverage (#2775) ─────────────────────────────────────────────

/**
 * How much of the harvest the Prognose actually covers (#2775): of the members
 * with at least one finite pick (``harvesting``), how many carry at least one
 * forecast point (``covered``). Derived from the SAME ``OutputActual[]`` /
 * ``OutputForecastPoint[]`` the chart consumes — one source, never a second
 * derivation that could disagree with the plotted line (#2413 discipline).
 *
 * A pick-less forecasting member counts in NEITHER number (WF: 14 forecasting,
 * 13 of them picking → "13 von 27"). Untagged forecast points cover every
 * harvesting member — the same GROUP_ALL semantics as ``widenedIstCum``'s
 * guard, so the hint can never claim a gap the widening does not have.
 *
 * ASSUMES finite, chart-drawable forecast values: any forecast point passed in
 * counts as coverage, so the caller must pre-filter to the points the chart
 * actually draws (dated, finite, inside the axis window) — feeding raw
 * unvalidated points would claim coverage the plotted line silently drops.
 */
export function forecastCoverage(
  actuals: OutputActual[],
  forecasts: OutputForecastPoint[],
): { covered: number; harvesting: number } {
  const harvesting = new Set<string>();
  for (const a of actuals) {
    if (Number.isFinite(a.qty)) harvesting.add(memberKey(a.seriesId));
  }
  const forecastKeys = new Set(forecasts.map((f) => memberKey(f.seriesId)));
  const covered = forecastKeys.has(GROUP_ALL)
    ? harvesting.size
    : [...harvesting].filter((k) => forecastKeys.has(k)).length;
  return { covered, harvesting: harvesting.size };
}

/**
 * Coverage-hint copy (#2775), the ``ohneSorteHintText`` pattern: a plain German
 * sentence disclosing that the aggregate "erwartetes Saisonende" is a partial
 * sum. ``null`` under full coverage — the caller additionally gates on a
 * forecast existing and on the aggregate (non-field) view.
 */
export function forecastCoverageHintText(covered: number, harvesting: number): string | null {
  if (covered >= harvesting) return null;
  return `Prognose liegt nur für ${covered} von ${harvesting} erntenden ${
    harvesting === 1 ? 'Schlag' : 'Schlägen'
  } vor.`;
}

// ── Ampel verdict ─────────────────────────────────────────────────────────

const GREEN_THRESHOLD = 0.95;
const YELLOW_THRESHOLD = 0.85;

function formatQty(value: number, unit: string): string {
  const n = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(Math.round(value));
  return `${n} ${unit}`;
}

/**
 * Ampel verdict: is the actual harvest keeping pace with the forecast at the
 * latest bucket that carries actual data? "Auf Kurs" (green) when Ist is at or
 * near the cumulative Prognose, otherwise "im Rückstand" (yellow/red).
 */
export function buildOutputVerdict(rows: OutputChartRow[], measureUnit: string): OutputVerdict {
  // Latest bucket that actually has Ist data.
  let atBucket: string | null = null;
  let actualToDate: number | null = null;
  let forecastToDate: number | null = null;
  for (const r of rows) {
    if (r.actualCum != null) {
      atBucket = r.bucket;
      actualToDate = r.actualCum;
      forecastToDate = r.forecastCum;
    }
  }

  if (actualToDate == null) {
    return {
      level: 'neutral',
      headline: 'Noch keine Ernte',
      sentence: 'Für diese Kultur ist noch keine Ernte erfasst.',
      actualToDate: null,
      forecastToDate: null,
      ratio: null,
      atBucket: null,
    };
  }

  if (forecastToDate == null || forecastToDate <= 0) {
    // #2775 — a Prognose that BEGINS only after the last pick (the disjoint
    // late-season shape): compare-at-last-Ist is undefined, but a forecast line
    // IS plotted, so the stock "ohne Prognose" copy would contradict the chart
    // and the legend. Name the start instead — via the row's LABEL, so the copy
    // is grain-safe. Scoped to future-START only: a forecast that ENDED before
    // the last pick keeps the stock copy below (deliberate non-goal).
    const firstForecastRow = rows.find((r) => r.forecastCum != null);
    if (firstForecastRow != null && atBucket != null && firstForecastRow.bucket > atBucket) {
      return {
        level: 'neutral',
        headline: 'Noch kein Vergleich',
        sentence: `Bisher ${formatQty(actualToDate, measureUnit)} geerntet — die Prognose beginnt erst ab ${firstForecastRow.label} und lässt sich noch nicht mit der Ernte vergleichen.`,
        actualToDate,
        forecastToDate,
        ratio: null,
        atBucket,
      };
    }
    return {
      level: 'neutral',
      headline: 'Kein Vergleich',
      sentence: `Bisher ${formatQty(actualToDate, measureUnit)} geerntet — ohne Prognose blende oben rechts die Vorjahres-Kurven ein, um den Verlauf einzuordnen.`,
      actualToDate,
      forecastToDate,
      ratio: null,
      atBucket,
    };
  }

  const ratio = actualToDate / forecastToDate;
  const actualStr = formatQty(actualToDate, measureUnit);
  const forecastStr = formatQty(forecastToDate, measureUnit);

  if (ratio >= GREEN_THRESHOLD) {
    return {
      level: 'green',
      headline: 'Auf Kurs',
      sentence: `Du liegst auf Kurs — bisher ${actualStr} geerntet gegenüber ${forecastStr} Prognose.`,
      actualToDate,
      forecastToDate,
      ratio,
      atBucket,
    };
  }
  if (ratio >= YELLOW_THRESHOLD) {
    return {
      level: 'yellow',
      headline: 'Leicht im Rückstand',
      sentence: `Du liegst leicht im Rückstand — bisher ${actualStr} geerntet, die Prognose lag bei ${forecastStr}.`,
      actualToDate,
      forecastToDate,
      ratio,
      atBucket,
    };
  }
  return {
    level: 'red',
    headline: 'Im Rückstand',
    sentence: `Du liegst im Rückstand — bisher ${actualStr} geerntet, die Prognose lag bei ${forecastStr}.`,
    actualToDate,
    forecastToDate,
    ratio,
    atBucket,
  };
}
